/**
 * Calculation utilities — pure-функции бизнес-логики расчётов смет.
 *
 * Phase 2 modularization Sprint 4 (2026-05-07).
 *
 * Вся логика идентична `ProfessionalQuoteCalculator.calculate*()` —
 * на каждый class-method сделана pure-версия принимающая
 * `services`/`state` явно (вместо `this.*`). Это позволяет:
 *   1. Unit-тестировать формулы без браузера
 *   2. Использовать одну реализацию в frontend и backend (data_hash, рендер PDF)
 *   3. Гарантировать что class-методы и pure-функции дают идентичный результат
 *
 * ⚠️ КРИТИЧЕСКИЙ КОД — формулы согласованы с клиентом и НЕ должны меняться
 * без явного запроса. См. CLAUDE.md "Бизнес-логика расчётов".
 */
(function (global) {
    'use strict';

    /**
     * Стоимость одной услуги с её индивидуальной наценкой.
     * @param {{price: number, quantity: number, markup: number}} service
     * @returns {number}
     */
    function calculateServiceTotal(service) {
        const price = Number(service.price) || 0;
        const quantity = Number(service.quantity) || 0;
        const markup = Number(service.markup) || 0;
        const baseTotal = price * quantity;
        return baseTotal * (1 + markup / 100);
    }

    /**
     * Базовая (без наценок) стоимость услуги.
     */
    function calculateServiceBase(service) {
        return (Number(service.price) || 0) * (Number(service.quantity) || 0);
    }

    /**
     * Объединить все секции (services, hotels, flights, otherServices)
     * в один массив. Удобно для последующих reduce-итераций.
     */
    function flattenSections(state) {
        return [
            ...(state.services || []),
            ...(state.hotels || []),
            ...(state.flights || []),
            ...(state.otherServices || [])
        ];
    }

    /**
     * Базовая стоимость всех услуг по всем секциям (без наценок и налогов).
     * @param {Object} state - { services, hotels, flights, otherServices }
     * @returns {number}
     */
    function calculateBaseCost(state) {
        return flattenSections(state).reduce(
            (sum, s) => sum + calculateServiceBase(s),
            0
        );
    }

    /**
     * Сумма исключительно для скрытой наценки — учитывает только
     * услуги с `excludeFromMarkup !== true`.
     */
    function calculateBaseCostForHiddenMarkup(state) {
        return flattenSections(state).reduce((sum, s) => {
            if (s.excludeFromMarkup) return sum;
            return sum + calculateServiceBase(s);
        }, 0);
    }

    /**
     * Прибыль = сумма индивидуальных наценок + скрытая наценка.
     * @param {Object} state
     * @returns {number}
     */
    function calculateTotalProfit(state) {
        const items = flattenSections(state);

        const individualMarkupAmount = items.reduce((sum, s) => {
            const base = calculateServiceBase(s);
            const withMarkup = calculateServiceTotal(s);
            return sum + (withMarkup - base);
        }, 0);

        const hiddenBase = calculateBaseCostForHiddenMarkup(state);
        const hiddenMarkup = Number(state.hiddenMarkup) || 0;
        const hiddenMarkupAmount = hiddenBase * (hiddenMarkup / 100);

        return individualMarkupAmount + hiddenMarkupAmount;
    }

    /**
     * Итого клиенту = сумма с индивид. наценками + скрытая наценка + НДС.
     * @param {Object} state
     * @returns {number}
     */
    function calculateClientTotal(state) {
        const items = flattenSections(state);

        const totalWithIndividualMarkup = items.reduce(
            (sum, s) => sum + calculateServiceTotal(s),
            0
        );

        const hiddenBase = calculateBaseCostForHiddenMarkup(state);
        const hiddenMarkup = Number(state.hiddenMarkup) || 0;
        const hiddenMarkupAmount = hiddenBase * (hiddenMarkup / 100);

        const taxRate = Number(state.taxRate) || 0;
        const taxAmount = totalWithIndividualMarkup * (taxRate / 100);

        return totalWithIndividualMarkup + hiddenMarkupAmount + taxAmount;
    }

    /**
     * Длительность активности по start/end time строкам "HH:MM".
     * Поддерживает overnight (если end < start, добавляем 24h).
     * @returns {string} "X ч Y мин" / "X ч" / "Y мин" / "—"
     */
    function calculateDuration(startTime, endTime) {
        if (!startTime || !endTime) return '—';
        try {
            const [sh, sm] = String(startTime).split(':').map(Number);
            const [eh, em] = String(endTime).split(':').map(Number);
            if (isNaN(sh) || isNaN(eh)) return '—';
            const startMin = sh * 60 + sm;
            let endMin = eh * 60 + em;
            let dur = endMin - startMin;
            if (dur < 0) dur += 24 * 60;
            const hours = Math.floor(dur / 60);
            const minutes = dur % 60;
            if (hours === 0) return `${minutes} мин`;
            if (minutes === 0) return `${hours} ч`;
            return `${hours} ч ${minutes} мин`;
        } catch (e) {
            return '—';
        }
    }

    const CalcUtils = {
        calculateServiceTotal,
        calculateServiceBase,
        flattenSections,
        calculateBaseCost,
        calculateBaseCostForHiddenMarkup,
        calculateTotalProfit,
        calculateClientTotal,
        calculateDuration
    };

    if (typeof module !== 'undefined' && module.exports) {
        module.exports = CalcUtils;
    }
    if (global) {
        global.CalcUtils = CalcUtils;
    }
})(typeof window !== 'undefined' ? window : globalThis);
